import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  Input,
  ViewEncapsulation,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { EnergySourceEnum } from '@clean-code/models';
import { indicate } from '@clean-code/shared/common';
import { ControlValueAccessorConnector } from '@clean-code/shared/components/ui-form-controls';
import { TranslocoModule } from '@jsverse/transloco';
import { NgSelectModule } from '@ng-select/ng-select';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
} from 'rxjs/operators';
import { KamDropDownService } from './kam-dropdown.service';

@Component({
  selector: 'bo-kam-dropdown',
  templateUrl: './kam-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ReactiveFormsModule, NgSelectModule, TranslocoModule, AsyncPipe],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KamDropdownComponent),
      multi: true,
    },
  ],
})
export class KamDropdownComponent extends ControlValueAccessorConnector {
  private readonly companyService = inject(KamDropDownService);
  public energySource$ = new BehaviorSubject<string>(null);
  public searchTerm$ = new BehaviorSubject<string>('');
  public loading$ = new BehaviorSubject<boolean>(false);

  @Input()
  public set energySource(value: string) {
    this.energySource$.next(value);
  }

  public data$ = combineLatest([
    this.energySource$,
    this.searchTerm$.pipe(debounceTime(300), distinctUntilChanged()),
  ]).pipe(
    switchMap(([value, search]: [string, string]) => {
      const searchTerm = search ?? '';

      if (value === EnergySourceEnum.Gas) {
        return this.companyService.getAllGasKeyAccountManager$(searchTerm).pipe(
          indicate(this.loading$),
          catchError(() => of([])),
        );
      }

      return this.companyService.getAllPowerKeyAccountManager$(searchTerm).pipe(
        indicate(this.loading$),
        catchError(() => of([])),
      );
    }),
  );
}
